import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Shield, Buildings, UploadSimple, X, FileText, CheckCircle, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { titleCase } from "@/lib/textFormat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputCls =
  "h-9 px-2.5 text-sm rounded-sm border border-[#D0D5DD] text-[#101828] focus:outline-none focus:border-[#004B87] focus:ring-1 focus:ring-[#004B87] w-full";
const labelCls = "font-heading text-xs font-bold text-[#475467] uppercase tracking-wide block mb-1";
const sectionCls = "bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-3";
const sectionTitleCls = "font-heading text-sm font-bold text-[#1D2939] flex items-center gap-2 pb-2 border-b border-[#EAECF0]";

const errMsg = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback || err.message;
  if (typeof detail === "string") return detail;
  return Object.values(detail).flat().join(", ");
};

const emptyForm = {
  company_name: "", is_gst_registered: true, gstin: "", pan: "",
  contact_name: "", contact_email: "", contact_phone: "", secondary_contact_name: "", secondary_phone: "",
  address_line: "", city: "", state: "", pincode: "",
  category: "", currency: "INR",
  bank_account_number: "", bank_ifsc: "", bank_account_holder_name: "",
  bank_details_confirmed: false, bank_cheque_uploaded: false,
};

const SUPPLIER_CATEGORIES = ["Raw Material", "Packaging", "Hardware", "Consumable", "Plastic", "Capital", "Die & Tools"];

export default function SupplierSelfRegisterPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [linkValid, setLinkValid] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [gstChecking, setGstChecking] = useState(false);
  const [gstResult, setGstResult] = useState(null);
  const [panChecking, setPanChecking] = useState(false);
  const [panResult, setPanResult] = useState(null);
  const [readingCheque, setReadingCheque] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/supplier-onboarding-requests/public/${token}`);
        setLinkValid(true);
        setStatus(data.status);
        setForm((f) => ({ ...f, contact_email: data.supplier_email || "" }));
      } catch (err) {
        setLinkValid(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setTitleCaseField = (key, value) => setForm((f) => ({ ...f, [key]: titleCase(value) }));
  // Any edit to a bank field invalidates a prior confirmation.
  const setBankField = (key, value) => setForm((f) => ({ ...f, [key]: value, bank_details_confirmed: false }));
  const addFiles = (fileList) => setFiles((prev) => [...prev, ...Array.from(fileList)]);
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));
  const fieldError = (key) => (errors[key] || [])[0];
  const errCls = (key) => (fieldError(key) ? "border-[#B42318] focus:border-[#B42318] focus:ring-[#B42318]" : "");
  const bankFieldsFilled = !!(form.bank_account_holder_name && form.bank_account_number && form.bank_ifsc);

  const checkGstin = async () => {
    if (!form.gstin) return;
    setGstChecking(true);
    setGstResult(null);
    try {
      const { data } = await axios.post(`${API}/supplier-onboarding-requests/public/${token}/verify-gstin`, { gstin: form.gstin });
      setGstResult({ ok: true, ...data });
    } catch (err) {
      setGstResult({ ok: false, message: errMsg(err, "Could not verify this GSTIN") });
    } finally {
      setGstChecking(false);
    }
  };

  const checkPan = async () => {
    if (!form.pan) return;
    setPanChecking(true);
    setPanResult(null);
    try {
      const { data } = await axios.post(`${API}/supplier-onboarding-requests/public/${token}/verify-pan`, { pan: form.pan, name: form.company_name || null });
      setPanResult({ ok: true, ...data });
    } catch (err) {
      setPanResult({ ok: false, message: errMsg(err, "Could not verify this PAN") });
    } finally {
      setPanChecking(false);
    }
  };

  // Feb 2026, user's explicit ask: AI-read bank details off an uploaded
  // cancelled cheque instead of typing them in - mirrors the same
  // feature on the purchaser-facing form (SupplierOnboardingRequestPage.jsx).
  const readCheque = async (file) => {
    setReadingCheque(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/supplier-onboarding-requests/public/${token}/read-cheque`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const anyFieldRead = data.bank_account_number || data.bank_ifsc || data.bank_account_holder_name;
      setForm((f) => ({
        ...f,
        bank_account_number: data.bank_account_number || f.bank_account_number,
        bank_ifsc: data.bank_ifsc || f.bank_ifsc,
        bank_account_holder_name: data.bank_account_holder_name || f.bank_account_holder_name,
        bank_details_confirmed: false,
        bank_cheque_uploaded: true,
      }));
      addFiles([file]);
      if (data.warning && !anyFieldRead) {
        toast.error("Could not read cheque", { description: data.warning });
      } else if (data.warning) {
        toast.warning("Cheque partially read", { description: data.warning });
      } else {
        toast.success("Bank details read from cheque", { description: "Please double-check the fields before submitting." });
      }
    } catch (err) {
      toast.error("Could not read the cheque", { description: errMsg(err, "Please type the bank details in manually.") });
    } finally {
      setReadingCheque(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setErrors({});
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v === null || v === undefined ? "" : v));
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/supplier-onboarding-requests/public/${token}/submit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        setErrors(detail);
        toast.dismiss();
        toast.error("Please fix the highlighted fields");
      } else {
        toast.dismiss();
        toast.error("Submission failed", { description: detail || err.message });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const Header = () => (
    <header className="min-h-16 bg-[#0E7C86] flex items-center px-4 sm:px-6 shrink-0">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
          <Shield size={18} weight="fill" className="text-white" />
        </div>
        <span className="font-heading text-[16px] font-bold text-white tracking-tight">Supplier Registration</span>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F4F7] flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center text-[#475467] text-sm" data-testid="self-register-loading">Loading...</div>
      </div>
    );
  }

  if (!linkValid) {
    return (
      <div className="min-h-screen bg-[#F2F4F7] flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white border border-[#D0D5DD] rounded-sm p-6 max-w-md text-center" data-testid="self-register-invalid-link">
            <p className="text-sm text-[#475467]">This registration link is invalid or has expired. Please contact the person who sent it to you for a new one.</p>
          </div>
        </div>
      </div>
    );
  }

  if (status !== "awaiting_supplier_fill" || submitted) {
    return (
      <div className="min-h-screen bg-[#F2F4F7] flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white border border-[#D0D5DD] rounded-sm p-6 max-w-md text-center space-y-2" data-testid="self-register-already-submitted">
            <CheckCircle size={32} weight="fill" className="text-[#10B981] mx-auto" />
            <p className="text-sm font-medium text-[#1D2939]">Thank you - your details have been submitted.</p>
            <p className="text-xs text-[#475467]">Please share the PIN code from your invitation email with the person who sent it, so they can confirm your submission.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F4F7] flex flex-col">
      <Header />
      <main className="flex-1 overflow-auto p-4">
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="flex items-center gap-2">
            <Buildings size={18} weight="fill" className="text-[#004B87]" />
            <h1 className="font-heading text-base font-bold">Supplier Registration Form</h1>
          </div>
          <p className="text-sm text-[#475467] -mt-2">
            Please fill in your company details below. Once submitted, share the PIN code from your invitation
            email with the person who invited you so they can confirm this submission.
          </p>

          <div className={sectionCls} data-testid="self-register-company-section">
            <div className={sectionTitleCls}>Company Details</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Company Name *</label>
                <input className={`${inputCls} ${errCls("company_name")}`} value={form.company_name}
                  onChange={(e) => { setTitleCaseField("company_name", e.target.value); setPanResult(null); }} data-testid="self-register-company-name-input" />
                {fieldError("company_name") && <p className="text-xs text-[#B42318] mt-1">{fieldError("company_name")}</p>}
              </div>
              <div>
                <label className={labelCls}>Is GST Registered?</label>
                <select className={inputCls} value={form.is_gst_registered ? "yes" : "no"}
                  onChange={(e) => setField("is_gst_registered", e.target.value === "yes")} data-testid="self-register-gst-registered-select">
                  <option value="yes">Yes</option>
                  <option value="no">No (unregistered / composition)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Category *</label>
                <select className={`${inputCls} ${errCls("category")}`} value={form.category}
                  onChange={(e) => setField("category", e.target.value)} data-testid="self-register-category-select">
                  <option value="">Select category</option>
                  {SUPPLIER_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {fieldError("category") && <p className="text-xs text-[#B42318] mt-1">{fieldError("category")}</p>}
              </div>
              <div className="col-span-2">
                <label className={labelCls}>GSTIN {form.is_gst_registered ? "*" : ""}</label>
                <div className="flex gap-2">
                  <input className={`${inputCls} ${errCls("gstin")}`} value={form.gstin} placeholder="15-character GSTIN"
                    onChange={(e) => { setField("gstin", e.target.value.toUpperCase()); setGstResult(null); }} data-testid="self-register-gstin-input" />
                  <Button type="button" onClick={checkGstin} disabled={gstChecking || !form.gstin} className="h-9 shrink-0 bg-[#0E7C86] hover:bg-[#0B6670] text-white text-xs rounded-sm" data-testid="self-register-verify-gstin-button">
                    <MagnifyingGlass size={13} className="mr-1" /> {gstChecking ? "Checking..." : "Verify"}
                  </Button>
                </div>
                {fieldError("gstin") && <p className="text-xs text-[#B42318] mt-1">{fieldError("gstin")}</p>}
                {gstResult && gstResult.ok && (
                  <p className="text-xs text-[#0B7A56] mt-1" data-testid="self-register-gstin-result-ok">
                    {gstResult.legal_name} ({gstResult.trade_name}) - {gstResult.status} - {gstResult.address}
                  </p>
                )}
                {gstResult && !gstResult.ok && (
                  <p className="text-xs text-[#B42318] mt-1" data-testid="self-register-gstin-result-fail">{gstResult.message}</p>
                )}
              </div>
              <div>
                <label className={labelCls}>PAN {!form.is_gst_registered ? "*" : ""}</label>
                <div className="flex gap-2">
                  <input className={`${inputCls} ${errCls("pan")}`} value={form.pan}
                    onChange={(e) => { setField("pan", e.target.value.toUpperCase()); setPanResult(null); }} data-testid="self-register-pan-input" />
                  <Button type="button" onClick={checkPan} disabled={panChecking || !form.pan} className="h-9 shrink-0 bg-[#0E7C86] hover:bg-[#0B6670] text-white text-xs rounded-sm" data-testid="self-register-verify-pan-button">
                    <MagnifyingGlass size={13} className="mr-1" /> {panChecking ? "Checking..." : "Verify"}
                  </Button>
                </div>
                {fieldError("pan") && <p className="text-xs text-[#B42318] mt-1">{fieldError("pan")}</p>}
                {panResult && panResult.ok && (
                  <p className={`text-xs mt-1 ${!panResult.verified ? "text-[#B42318]" : panResult.name_matched === false ? "text-[#B8860B]" : "text-[#0B7A56]"}`} data-testid="self-register-pan-result-ok">
                    {panResult.verified ? `${panResult.name} - ${panResult.status}` : "Not found in the Income Tax database"}
                    {panResult.name_match_score != null && ` (name match: ${panResult.name_match_score}%${panResult.name_matched === false ? " - mismatch" : ""})`}
                  </p>
                )}
                {panResult && !panResult.ok && (
                  <p className="text-xs text-[#B42318] mt-1" data-testid="self-register-pan-result-fail">{panResult.message}</p>
                )}
              </div>
            </div>
          </div>

          <div className={sectionCls} data-testid="self-register-contact-section">
            <div className={sectionTitleCls}>Primary Contact</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Contact Name *</label>
                <input className={`${inputCls} ${errCls("contact_name")}`} value={form.contact_name}
                  onChange={(e) => setTitleCaseField("contact_name", e.target.value)} data-testid="self-register-contact-name-input" />
                {fieldError("contact_name") && <p className="text-xs text-[#B42318] mt-1">{fieldError("contact_name")}</p>}
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input className={`${inputCls} ${errCls("contact_email")}`} value={form.contact_email}
                  onChange={(e) => setField("contact_email", e.target.value)} data-testid="self-register-contact-email-input" />
                {fieldError("contact_email") && <p className="text-xs text-[#B42318] mt-1">{fieldError("contact_email")}</p>}
              </div>
              <div>
                <label className={labelCls}>Phone (10-digit mobile) *</label>
                <input className={`${inputCls} ${errCls("contact_phone")}`} value={form.contact_phone}
                  onChange={(e) => setField("contact_phone", e.target.value)} data-testid="self-register-contact-phone-input" />
                {fieldError("contact_phone") && <p className="text-xs text-[#B42318] mt-1">{fieldError("contact_phone")}</p>}
              </div>
              <div>
                <label className={labelCls}>Secondary Contact Name *</label>
                <input className={`${inputCls} ${errCls("secondary_contact_name")}`} value={form.secondary_contact_name}
                  onChange={(e) => setTitleCaseField("secondary_contact_name", e.target.value)} data-testid="self-register-secondary-contact-name-input" />
                {fieldError("secondary_contact_name") && <p className="text-xs text-[#B42318] mt-1">{fieldError("secondary_contact_name")}</p>}
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Secondary Phone (10-digit mobile) *</label>
                <input className={`${inputCls} ${errCls("secondary_phone")}`} value={form.secondary_phone}
                  onChange={(e) => setField("secondary_phone", e.target.value)} data-testid="self-register-secondary-phone-input" />
                {fieldError("secondary_phone") && <p className="text-xs text-[#B42318] mt-1">{fieldError("secondary_phone")}</p>}
              </div>
            </div>
          </div>

          <div className={sectionCls} data-testid="self-register-address-section">
            <div className={sectionTitleCls}>Registered Address</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Street Address *</label>
                <input className={`${inputCls} ${errCls("address_line")}`} value={form.address_line}
                  onChange={(e) => setTitleCaseField("address_line", e.target.value)} data-testid="self-register-address-line-input" />
                {fieldError("address_line") && <p className="text-xs text-[#B42318] mt-1">{fieldError("address_line")}</p>}
              </div>
              <div>
                <label className={labelCls}>City *</label>
                <input className={`${inputCls} ${errCls("city")}`} value={form.city}
                  onChange={(e) => setTitleCaseField("city", e.target.value)} data-testid="self-register-city-input" />
                {fieldError("city") && <p className="text-xs text-[#B42318] mt-1">{fieldError("city")}</p>}
              </div>
              <div>
                <label className={labelCls}>State *</label>
                <input className={`${inputCls} ${errCls("state")}`} value={form.state}
                  onChange={(e) => setTitleCaseField("state", e.target.value)} data-testid="self-register-state-input" />
                {fieldError("state") && <p className="text-xs text-[#B42318] mt-1">{fieldError("state")}</p>}
              </div>
              <div>
                <label className={labelCls}>PIN Code *</label>
                <input className={`${inputCls} ${errCls("pincode")}`} value={form.pincode}
                  onChange={(e) => setField("pincode", e.target.value)} data-testid="self-register-pincode-input" />
                {fieldError("pincode") && <p className="text-xs text-[#B42318] mt-1">{fieldError("pincode")}</p>}
              </div>
            </div>
          </div>

          <div className={sectionCls} data-testid="self-register-bank-section">
            <div className={sectionTitleCls}>Bank Details *</div>
            <label
              htmlFor="self-register-cheque-input"
              className={`flex items-center justify-center gap-2 border border-dashed rounded-sm py-3 text-sm ${
                readingCheque
                  ? "border-[#D0D5DD] text-[#98A2B3] cursor-wait"
                  : form.bank_cheque_uploaded
                  ? "border-[#12B76A] text-[#12B76A] hover:bg-[#ECFDF3]"
                  : "border-[#004B87] text-[#004B87] cursor-pointer hover:bg-[#EFF8FF]"
              }`}
              data-testid="self-register-cheque-dropzone"
            >
              {form.bank_cheque_uploaded ? <CheckCircle size={16} weight="fill" /> : <UploadSimple size={16} />}{" "}
              {readingCheque
                ? "Reading cheque..."
                : form.bank_cheque_uploaded
                ? "Cheque attached - click to replace"
                : "Upload a cancelled cheque * (reads the fields below automatically)"}
            </label>
            <input id="self-register-cheque-input" type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" disabled={readingCheque}
              onChange={(e) => { if (e.target.files[0]) readCheque(e.target.files[0]); e.target.value = ""; }}
              data-testid="self-register-cheque-input" />
            {fieldError("bank_cheque_uploaded") && <p className="text-xs text-[#B42318]">{fieldError("bank_cheque_uploaded")}</p>}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Account Holder Name *</label>
                <input className={`${inputCls} ${errCls("bank_account_holder_name")}`} value={form.bank_account_holder_name}
                  onChange={(e) => setBankField("bank_account_holder_name", e.target.value)} data-testid="self-register-bank-holder-input" />
                {fieldError("bank_account_holder_name") && <p className="text-xs text-[#B42318] mt-1">{fieldError("bank_account_holder_name")}</p>}
              </div>
              <div>
                <label className={labelCls}>Account Number *</label>
                <input className={`${inputCls} ${errCls("bank_account_number")}`} value={form.bank_account_number}
                  onChange={(e) => setBankField("bank_account_number", e.target.value)} data-testid="self-register-bank-account-input" />
                {fieldError("bank_account_number") && <p className="text-xs text-[#B42318] mt-1">{fieldError("bank_account_number")}</p>}
              </div>
              <div>
                <label className={labelCls}>IFSC Code *</label>
                <input className={`${inputCls} ${errCls("bank_ifsc")}`} value={form.bank_ifsc}
                  onChange={(e) => setBankField("bank_ifsc", e.target.value.toUpperCase())} data-testid="self-register-bank-ifsc-input" />
                {fieldError("bank_ifsc") && <p className="text-xs text-[#B42318] mt-1">{fieldError("bank_ifsc")}</p>}
              </div>
            </div>
            <label
              className={`flex items-start gap-2 text-sm pt-1 ${
                bankFieldsFilled && form.bank_cheque_uploaded ? "text-[#1D2939] cursor-pointer" : "text-[#98A2B3] cursor-not-allowed"
              }`}
            >
              <input
                type="checkbox"
                className="mt-0.5"
                disabled={!bankFieldsFilled || !form.bank_cheque_uploaded}
                checked={form.bank_details_confirmed}
                onChange={(e) => setField("bank_details_confirmed", e.target.checked)}
                data-testid="self-register-bank-confirm-checkbox"
              />
              I have reviewed these bank details and confirm they are correct.
            </label>
            {fieldError("bank_details_confirmed") && <p className="text-xs text-[#B42318] mt-1">{fieldError("bank_details_confirmed")}</p>}
          </div>

          <div className={sectionCls} data-testid="self-register-attachments-section">
            <div className={sectionTitleCls}>Supporting Attachments</div>
            <label
              htmlFor="self-register-file-input"
              className="flex items-center justify-center gap-2 border border-dashed border-[#D0D5DD] rounded-sm py-6 cursor-pointer text-[#475467] text-sm hover:border-[#004B87] hover:text-[#004B87]"
              data-testid="self-register-file-dropzone"
            >
              <UploadSimple size={16} /> Click to attach GST certificate, PAN, cancelled cheque, etc.
            </label>
            <input id="self-register-file-input" type="file" multiple className="hidden"
              onChange={(e) => addFiles(e.target.files)} data-testid="self-register-file-input" />
            {files.length > 0 && (
              <div className="space-y-1.5">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-[#F9FAFB] border border-[#EAECF0] rounded-sm px-2.5 py-1.5" data-testid={`self-register-attachment-row-${i}`}>
                    <span className="flex items-center gap-1.5 text-[#344054]"><FileText size={13} /> {f.name}</span>
                    <button type="button" onClick={() => removeFile(i)} className="text-[#98A2B3] hover:text-[#B42318]" data-testid={`self-register-attachment-remove-${i}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button
            type="button"
            onClick={submit}
            disabled={submitting || !form.bank_details_confirmed || !form.bank_cheque_uploaded}
            className="h-10 w-full bg-[#004B87] hover:bg-[#003A6A] text-white text-sm rounded-sm"
            data-testid="self-register-submit-button"
          >
            {submitting ? "Submitting..." : "Submit Registration"}
          </Button>
        </div>
      </main>
    </div>
  );
}
